<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { usePlacesAutocomplete } from '@/composables/usePlacesAutocomplete';
import { useWidgetStore } from '@/stores/widget';
import { storeToRefs } from 'pinia';
import { useOptionsStore } from '@/stores/options';

const optionsStore = useOptionsStore();
const ageOptions = computed(() => optionsStore.offerOptionLists.age);

const currentPage = ref(0);
const pages = [
    { name: 'address_confirmation' },
    { name: 'property_details' },
    { name: 'property_ownership' },
    { name: 'selling_timeline' },
    { name: 'desired_value_range' },
    { name: 'contact_information' },
    { name: 'additional_information' },
];

const nextPage = () => {
    if (currentPage.value < pages.length - 1) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 0) {
        currentPage.value--;
    }
};

const font = ref('Arial');
const colorA = ref('#000000');
const colorB = ref('#ffffff');
const image = ref(null);
const maskStyle = ref('circle');
const borderRadius = ref('rounded_8');
const generatedCode = ref('');
const baseUrl = computed(() => (widget.value && widget.value.base_url) ? widget.value.base_url : '');
const widgetName = ref('');
const widgetTitle = ref('');
const ctaText = ref('');
const widgetDescription = ref('');
// Multiple widget sources (url + description)
const widgetSources = ref([
    { url: 'https://yourdomain.com', description: '' }
]);
// Autocomplete composable for preview address
const {
    address,
    predictions: placePredictions,
    loading: loadingPredictions,
    show: showPredictions,
    select: selectPrediction,
    handleBlur: handleAddressBlur,
    mapEmbedUrl,
} = usePlacesAutocomplete({
    apiKey: import.meta.env?.VITE_GOOGLE_MAPS_KEY,
    country: 'ca',
    fallback: 'Canada',
    minChars: 3,
    debounceMs: 300,
    zoomWhenEmpty: 4,
});

// mapEmbedUrl is provided by composable

const addWidgetSource = () => {
    widgetSources.value.push({ url: '', description: '' });
};
const removeWidgetSource = (idx) => {
    if (widgetSources.value.length === 1) { // keep at least one
        return;
    }
    widgetSources.value.splice(idx, 1);
};

const hydrateFormFields = (widgetData) => {
    if (!widgetData) {
        return;
    }
    font.value = widgetData.font_family || 'Arial';
    colorA.value = widgetData.color_a || '#000000';
    colorB.value = widgetData.color_b || '#ffffff';
    image.value = widgetData.image_path || null;
    maskStyle.value = widgetData.mask_style || 'circle';
    borderRadius.value = widgetData.border_radius || 'rounded_8';
    widgetName.value = widgetData.name || '';
    widgetTitle.value = widgetData.widget_title || '';
    ctaText.value = widgetData.cta_text || '';
    widgetDescription.value = widgetData.widget_description || '';
    if (Array.isArray(widgetData.widget_sources) && widgetData.widget_sources.length) {
        widgetSources.value = widgetData.widget_sources.map(ws => ({
            id: ws.id,
            url: ws.url || '',
            description: ws.description || '',
            is_active: ws.is_active !== undefined ? ws.is_active : true,
        }));
    } else {
        widgetSources.value = [{ url: 'https://yourdomain.com', description: '' }];
    }
};

const previewData = computed(() => ({
    font: font.value,
    colorA: colorA.value,
    colorB: colorB.value,
    image: image.value,
    maskStyle: maskStyle.value,
    borderRadius: borderRadius.value,
    widgetTitle: widgetTitle.value,
    ctaText: ctaText.value,
    widgetDescription: widgetDescription.value,
}));

const maskClass = computed(() => {
    switch (maskStyle.value) {
    case 'circle':
        return 'mask-circle';
    case 'square':
        return 'mask-square';
    case 'hexagon':
        return 'mask-hexagon';
    case 'shield':
        return 'mask-shield';
    default:
        return 'mask-circle';
    }
});

const isDragOver = ref(false);

const handleImageUpload = (e) => {
    let file;
    if (e.dataTransfer && e.dataTransfer.files.length) {
        file = e.dataTransfer.files[0];
    } else if (e.target && e.target.files.length) {
        file = e.target.files[0];
    }
    if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
            image.value = event.target.result;
        };
        reader.readAsDataURL(file);
    }
};

const handleDragOver = (e) => {
    e.preventDefault();
    isDragOver.value = true;
};

const handleDragLeave = (e) => {
    e.preventDefault();
    isDragOver.value = false;
};

const handleDrop = (e) => {
    e.preventDefault();
    isDragOver.value = false;
    handleImageUpload(e);
};

const widgetStore = useWidgetStore();
const { widget } = storeToRefs(widgetStore);

watch(
    () => widget.value,
    (widgetData) => {
        if (widgetData) {
            hydrateFormFields(widgetData);
            if (widgetData.public_key && baseUrl.value) {
                generatedCode.value = `<iframe src="${baseUrl.value}/api/widget/embed/key/${widget.value.public_key}"></iframe>`;
            }
        }
    },
    { deep: true, immediate: true }
);

onMounted(() => {
    if (widget.value) {
        hydrateFormFields(widget.value);
        if (widget.value.public_key && baseUrl.value) {
            generatedCode.value = `<iframe src="${baseUrl.value}/api/widget/embed/key/${widget.value.public_key}"></iframe>`;
        }
    }
});

const generateWidgetCode = () => {
    const slug = widgetName.value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    const configData = {
        name: widgetName.value,
        slug,
        widget_sources: widgetSources.value,
        font_family: font.value,
        color_a: colorA.value,
        color_b: colorB.value,
        image_path: image.value,
        mask_style: maskStyle.value,
        border_radius: borderRadius.value,
        widget_title: widgetTitle.value,
        cta_text: ctaText.value,
        widget_description: widgetDescription.value,
        widget_url: widgetSources.value[0]?.url,
        widget_source_description: widgetSources.value[0]?.description,
    };
    if (widget.value && widget.value.hash) {
        widgetStore.updateConfig(widget.value.hash, configData);
    } else {
        widgetStore.saveConfig(configData);
    }

    if (widget.value && widget.value.public_key && baseUrl.value) {
        generatedCode.value = `<iframe src="${baseUrl.value}/api/widget/embed/key/${widget.value.public_key}"></iframe>`;
    }
};

const copyCode = () => {
    if (generatedCode.value && window.navigator && window.navigator.clipboard && typeof window.navigator.clipboard.writeText === 'function') {
        window.navigator.clipboard.writeText(generatedCode.value);
    } else {
        const textarea = document.createElement('textarea');
        textarea.value = generatedCode.value;
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
        document.body.removeChild(textarea);
    }
};

const phone = ref('');
const formatPhone = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    if (digits.length > 0) {
        formatted += digits.substring(0, 3);
    }
    if (digits.length > 3) {
        formatted += '-' + digits.substring(3, 6);
    }
    if (digits.length > 6) {
        formatted += '-' + digits.substring(6, 10);
    }
    return formatted;
};
const handlePhoneInput = (e) => {
    phone.value = formatPhone(e.target.value);
};

const handleBackClick = () => {
    window.location.href = '/widgets';
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.widget_configuration') }}</template>
    <StyledButton
        class="mb-4 custom-tab-btn"
        @click="handleBackClick">
        {{ $t('label.back_to_widgets') }}
    </StyledButton>

    <div class="flex flex-col lg:flex-row gap-6">
        <!-- Left Column (Form) -->
        <div class="w-full lg:w-2/3 space-y-6">
            <!-- Widget Appearance -->
            <div class="p-4 rounded-lg border-2 border-grey-300 bg-white shadow">
                <h3 class="text-lg font-bold mb-4">{{ $t('label.widget_appearance') }}</h3>
                <div>
                    <label class="block font-semibold mb-1">{{ $t('label.widget_name') }}</label>
                    <StyledInput
                        v-model="widgetName"
                        name="name"
                        type="text"
                        class="flex-1 rounded mb-4" />
                </div>
                <div v-for="(source, idx) in widgetSources" :key="idx" class="mb-4 p-3 border-2 border-grey-300 rounded bg-grey-50 relative">
                    <div class="flex justify-between items-center mb-2">
                        <label class="block font-semibold">{{ $t('label.widget_source') }} #{{ idx + 1 }}</label>
                        <button
                            v-if="widgetSources.length > 1"
                            type="button"
                            class="text-red-600 text-base hover:underline"
                            @click="removeWidgetSource(idx)">
                            {{ $t('label.remove') }}
                        </button>
                    </div>
                    <label class="block font-semibold mb-1">{{ $t('label.widget_url') }}</label>
                    <StyledInput
                        v-model="source.url"
                        type="text"
                        class="flex-1 rounded mb-3" />
                    <label class="block font-semibold mb-1">{{ $t('label.widget_description') }}</label>
                    <StyledTextArea
                        v-model="source.description"
                        class="flex-1 rounded" />
                </div>
                <StyledButton type="button" class="mb-6 custom-tab-btn" @click="addWidgetSource">{{ $t('label.add_widget_url') }}</StyledButton>
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block font-semibold mb-1">{{ $t('label.primary_color') }}</label>
                        <div class="flex items-center gap-2">
                            <input
                                v-model="colorA"
                                type="color"
                                class="w-10 h-10 mb-4 cursor-pointer p-0 appearance-none">
                            <StyledInput
                                v-model="colorA"
                                type="text"
                                class="flex-1 rounded" />
                        </div>
                    </div>
                    <div>
                        <label class="block font-semibold mb-1">{{ $t('label.secondary_color') }}</label>
                        <div class="flex items-center gap-2">
                            <input
                                v-model="colorB"
                                type="color"
                                class="w-10 h-10 mb-4 cursor-pointer p-0 appearance-none">
                            <StyledInput
                                v-model="colorB"
                                type="text"
                                class="flex-1 rounded" />
                        </div>
                    </div>
                    <label class="font-bold">
                        {{ $t('label.font_family') }}
                        <StyledDropdown
                            v-model="font"
                            name="font"
                            :options="[
                                { label: $t('widget.font.arial'), value: 'Arial' },
                                { label: $t('widget.font.roboto'), value: 'Roboto' },
                                { label: $t('widget.font.times_new_roman'), value: 'Times New Roman' }
                            ]"
                            :placeholder="$t('label.select_font')" />
                    </label>
                    <label class="font-bold">
                        {{ $t('label.border_radius') }}
                        <StyledDropdown
                            v-model="borderRadius"
                            name="border_radius"
                            :options="[
                                { label: $t('widget.border_radius.rounded_8'), value: 'rounded_8' },
                                { label: $t('widget.border_radius.rounded_16'), value: 'rounded_16' },
                                { label: $t('widget.border_radius.square'), value: 'square' }
                            ]"
                            :placeholder="$t('label.select_border_radius')" />
                    </label>
                </div>
            </div>

            <!-- Widget Image Upload -->
            <div class="p-4 rounded-lg border-2 border-grey-300 bg-white shadow">
                <h3 class="text-lg font-semibold mb-4">{{ $t('label.widget_image_upload') }}</h3>
                <div class="flex flex-col items-center p-4 rounded">
                    <div
                        class="w-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors"
                        :class="isDragOver ? 'border-blue-500 bg-blue-50' : 'border-grey-300 bg-white'"
                        @dragover="handleDragOver"
                        @dragleave="handleDragLeave"
                        @drop="handleDrop"
                        @click="$refs.fileInput.click()">
                        <img v-if="image" :src="image" class="w-32 h-32 object-contain mt-2">
                        <span class="text-md text-grey-600 mb-2">{{ $t('label.drag_drop_or_click') }}</span>
                        <input
                            ref="fileInput"
                            type="file"
                            accept="image/*"
                            class="hidden"
                            @change="handleImageUpload">
                    </div>
                </div>

                <div class="mt-4">
                    <h6 class="font-bold text-md">{{ $t('label.image_mask_style') }}</h6>
                    <div>
                        <StyledRadio
                            v-model="maskStyle"
                            name="maskStyle"
                            color="primary"
                            radio-color="primary"
                            item-value="value"
                            item-title="label"
                            inline
                            :options="[
                                { value: 'square', label: $t('widget.mask.square') },
                                { value: 'circle', label: $t('widget.mask.circle') },
                                { value: 'hexagon', label: $t('widget.mask.hexagon') },
                                { value: 'shield', label: $t('widget.mask.shield') }
                            ]" />
                    </div>
                </div>
            </div>

            <!-- Cash Offer Widget Settings -->
            <div class="p-4 rounded-lg border-2 border-grey-300 bg-white shadow">
                <h3 class="text-lg font-semibold mb-4">{{ $t('label.cash_offer_widget_settings') }}</h3>
                <div class="grid grid-cols-2 gap-4">
                    <label class="font-bold">
                        {{ $t('label.widget_title') }}
                        <StyledInput v-model="widgetTitle" name="widget_title" @input="handlePreviewChange" />
                    </label>
                    <label class="font-bold">
                        {{ $t('label.cta_text') }}
                        <StyledInput v-model="ctaText" name="cta_text" @input="handlePreviewChange" />
                    </label>
                </div>
                <div class="mt-4">
                    <label class="font-bold">{{ $t('label.widget_description') }}</label>
                    <StyledTextArea 
                        v-model="widgetDescription" 
                        name="widget_description"
                        class="w-full rounded p-2 mt-1"
                        @input="handlePreviewChange" />
                </div>
            </div>
        </div>

        <!-- Right Column (Preview + Code) -->
        <div class="w-full lg:w-1/3 space-y-6">
            <!-- Widget Preview -->
            <div class="p-4 rounded-lg  border-2 border-grey-300 bg-white shadow">
                <h3 class="text-lg font-semibold mb-4">{{ $t('label.widget_preview') }}</h3>
                <div
                    class="border-2 border-grey-400 p-4 mx-auto"
                    :style="{
                        fontFamily: previewData.font,
                        background: previewData.colorB,
                        color: previewData.colorA,
                        borderRadius: previewData.borderRadius === 'rounded_8' ? '8px' : (previewData.borderRadius === 'rounded_16' ? '16px' : '0px')
                    }">
                    <div class="flex items-center gap-3 mb-2">
                        <img
                            v-if="image"
                            :src="image"
                            :alt="$t('widget.preview.image_alt')"
                            :class="['object-cover', maskClass]"
                            style="width:64px;height:64px">
                        <h5 class="font-bold" :style="{ fontFamily: previewData.font }">{{ widgetTitle || $t('widget.preview.title') }}</h5>
                    </div>
                    <p class="text-md mb-5 mt-2" :style="{ fontFamily: previewData.font }">{{ widgetDescription || $t('widget.preview.description') }}</p>
                    <!-- Multi-page widget fields -->
                    <template v-if="pages[currentPage].name === 'address_confirmation'">
                        <div>
                            {{ $t('widget.preview.property_address') }}
                            <div class="relative">
                                <input
                                    v-model="address"
                                    class="w-full p-2 bg-grey-100 mb-5 rounded custom-border"
                                    :placeholder="$t('widget.fields.property_address')"
                                    autocomplete="off"
                                    style="color: black;"
                                    @focus="showPredictions = address && address.length >= 3"
                                    @blur="handleAddressBlur">
                                <!-- Predictions dropdown -->
                                <div v-if="showPredictions" class="absolute left-0 right-0 z-50 bg-white border border-grey-300 rounded shadow max-h-60 overflow-auto">
                                    <div v-if="loadingPredictions" class="p-2 text-sm text-grey-600">{{ $t('label.loading') }}</div>
                                    <template v-else>
                                        <button
                                            v-for="p in placePredictions"
                                            :key="p.place_id"
                                            type="button"
                                            class="text-left w-full px-4 py-3 hover:bg-grey-100 text-base font-medium"
                                            @mousedown.prevent="selectPrediction(p)">{{ p.description }}</button>
                                        <div v-if="!placePredictions.length" class="p-2 text-sm text-grey-600">{{ $t('label.no_results') }}</div>
                                    </template>
                                </div>
                            </div>
                            <!-- Google Maps window -->
                            <iframe
                                :src="mapEmbedUrl"
                                width="100%"
                                height="200"
                                style="border:0;"
                                allowfullscreen=""
                                loading="lazy" />
                        </div>
                    </template>
                    <template v-else-if="pages[currentPage].name === 'property_details'">
                        <div>
                            {{ $t('widget.preview.please_select_the_type_of_property_that_best_describes_yours') }}
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option value="Detached">{{ $t('widget.property_type.detached') }}</option>
                                <option value="Att/Row/Twnhouse">{{ $t('widget.property_type.att_row_twnhouse') }}</option>
                                <option value="Duplex">{{ $t('widget.property_type.duplex') }}</option>
                                <option value="Multiplex">{{ $t('widget.property_type.multiplex') }}</option>
                                <option value="Condo Apt">{{ $t('widget.property_type.condo_apt') }}</option>
                            </select>
                        </div>
                        <div>
                            {{ $t('widget.preview.year_built') }}
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option v-for="option in ageOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            {{ $t('widget.preview.approx_sq_ft') }}
                            <input class="w-full p-2 bg-grey-100 mb-5 rounded custom-border" :placeholder="$t('widget.fields.approx_sq_ft')" style="color: black;">
                        </div>
                        <div>
                            {{ $t('widget.preview.bedrooms') }}
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option>{{ $t('widget.preview.1') }}</option>
                                <option>{{ $t('widget.preview.2') }}</option>
                                <option>{{ $t('widget.preview.3') }}</option>
                                <option>{{ $t('widget.preview.4') }}</option>
                                <option>{{ $t('widget.preview.5_plus') }}</option>
                            </select>
                        </div>
                        <div>
                            {{ $t('widget.preview.bathrooms') }}
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option>{{ $t('widget.preview.1') }}</option>
                                <option>{{ $t('widget.preview.1_5') }}</option>
                                <option>{{ $t('widget.preview.2') }}</option>
                                <option>{{ $t('widget.preview.2_5') }}</option>
                                <option>{{ $t('widget.preview.3') }}</option>
                                <option>{{ $t('widget.preview.3_5') }}</option>
                                <option>{{ $t('widget.preview.4') }}</option>
                                <option>{{ $t('widget.preview.4_5') }}</option>
                                <option>{{ $t('widget.preview.5_plus') }}</option>
                            </select>
                        </div>
                        <div>
                            {{ $t('widget.preview.basement_development') }}
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option value="Apartment">{{ $t('widget.preview.basement_suite') }}</option>
                                <option value="Crawl Space">{{ $t('widget.preview.no_basement') }}</option>
                                <option value="Fin W/O">{{ $t('widget.preview.walkout_finished') }}</option>
                                <option value="Finished">{{ $t('widget.preview.full_basement_fully_finished') }}</option>
                                <option value="Unfinished">{{ $t('widget.preview.full_unfinished') }}</option>
                                <option value="W/O">{{ $t('widget.preview.walkout_unfinished') }}</option>
                                <option value="Other">{{ $t('widget.preview.other') }}</option>
                            </select>
                        </div>
                        <div>
                            {{ $t('widget.preview.garage_parking') }}
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option value="Attached">{{ $t('widget.preview.single_attached') }}</option>
                                <option value="Detached">{{ $t('widget.preview.single_detached') }}</option>
                                <option value="Attached">{{ $t('widget.preview.double_attached') }}</option>
                                <option value="Detached">{{ $t('widget.preview.double_detached') }}</option>
                                <option value="Attached">{{ $t('widget.preview.triple_attached') }}</option>
                                <option value="Detached">{{ $t('widget.preview.triple_detached') }}</option>
                                <option value="Surface">{{ $t('widget.preview.single_car_stall') }}</option>
                                <option value="None">{{ $t('widget.preview.no_garage') }}</option>
                                <option value="Other">{{ $t('widget.preview.4_or_more') }}</option>
                            </select>
                        </div>
                    </template>
                    <template v-else-if="pages[currentPage].name === 'property_ownership'">
                        <div>
                            <!-- Ownership fields -->
                            <label>{{ $t('widget.preview.are_you_owner') }}</label>
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option :value="true">{{ $t('label.yes') }}</option>
                                <option :value="false">{{ $t('label.no') }}</option>
                            </select>
                            <label>{{ $t('widget.preview.are_you_working_with_agent') }}</label>
                            <select class="w-full custom-border bg-grey-100 p-2 mb-5 rounded" style="color: black;">
                                <option :value="true">{{ $t('label.yes') }}</option>
                                <option :value="false">{{ $t('label.no') }}</option>
                            </select>
                        </div>
                    </template>
                    <template v-else-if="pages[currentPage].name === 'selling_timeline'">
                        <div>
                            <label class="block mb-2 font-medium">{{ $t('widget.preview.timeline_for_selling') }}</label>
                            <div class="flex flex-col gap-2 mb-3">
                                <label class="flex items-center gap-2 bg-grey-100 custom-border p-2 rounded" style="color:black;">
                                    <input type="checkbox" disabled>
                                    <span>{{ $t('widget.preview.timeline_asap') }}</span>
                                </label>
                                <label class="flex items-center gap-2 bg-grey-100 custom-border p-2 rounded" style="color:black;">
                                    <input type="checkbox" disabled>
                                    <span>{{ $t('widget.preview.timeline_1_2_weeks') }}</span>
                                </label>
                                <label class="flex items-center gap-2 bg-grey-100 custom-border p-2 rounded" style="color:black;">
                                    <input type="checkbox" disabled>
                                    <span>{{ $t('widget.preview.timeline_1_month') }}</span>
                                </label>
                                <label class="flex items-center gap-2 bg-grey-100 custom-border p-2 rounded" style="color:black;">
                                    <input type="checkbox" disabled>
                                    <span>{{ $t('widget.preview.timeline_flexible') }}</span>
                                </label>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="pages[currentPage].name === 'desired_value_range'">
                        <div>
                            <label>{{ $t('widget.preview.what_do_you_believe_the_minimum_value_of_your_home_is') }}</label>
                            <input class="w-full p-2 bg-grey-100 mb-2 rounded custom-border" :placeholder="$t('widget.fields.min_value')" style="color: black;">
                            <label>{{ $t('widget.preview.what_do_you_believe_the_maximum_value_of_your_home_is') }}</label>
                            <input class="w-full p-2 bg-grey-100 mb-2 rounded custom-border" :placeholder="$t('widget.fields.max_value')" style="color: black;">
                        </div>
                    </template>
                    <template v-else-if="pages[currentPage].name === 'contact_information'">
                        <div>
                            <label>{{ $t('widget.preview.first_name') }}</label>
                            <input class="w-full p-2 bg-grey-100 mb-2 rounded custom-border" :placeholder="$t('widget.fields.first_name')" style="color: black;">
                            <label>{{ $t('widget.preview.last_name') }}</label>
                            <input class="w-full p-2 bg-grey-100 mb-2 rounded custom-border" :placeholder="$t('widget.fields.last_name')" style="color: black;">
                            <label>{{ $t('widget.preview.email') }}</label>
                            <input class="w-full p-2 bg-grey-100 mb-2 rounded custom-border" :placeholder="$t('widget.fields.email')" style="color: black;">
                            <label>{{ $t('widget.preview.phone') }}</label>
                            <input
                                v-model="phone"
                                class="w-full p-2 bg-grey-100 mb-2 rounded custom-border"
                                :placeholder="$t('widget.fields.phone')"
                                maxlength="12"
                                style="color: black;"
                                @input="handlePhoneInput">
                        </div>
                    </template>
                    <template v-else-if="pages[currentPage].name === 'additional_information'">
                        <div>
                            <label>{{ $t('widget.preview.additional_info') }}</label>
                            <textarea class="w-full p-2 bg-grey-100 mb-2 rounded custom-border" style="color: black;" />
                        </div>
                    </template>
                    <!-- Navigation buttons -->
                    <div class="flex mt-4">
                        <button 
                            v-if="!(pages[currentPage].name === 'address_confirmation')" 
                            class="border px-3 py-1 rounded text-white" 
                            :style="{ background: colorA }" 
                            :disabled="currentPage === 0" 
                            @click="prevPage">{{ $t('label.previous') }}
                        </button>
                        <button 
                            v-if="!(pages[currentPage].name === 'additional_information')" 
                            class="border px-3 py-1 rounded text-white ml-auto" 
                            :style="{ background: colorA }" 
                            @click="nextPage">{{ $t('label.next') }}
                        </button>
                    </div>
                    <!-- Only show CTA button on last page -->
                    <button v-if="currentPage === pages.length - 1" class="mt-4 text-white px-4 py-2 rounded w-full" :style="{ background: colorA }">{{ ctaText || $t('widget.preview.cta') }}</button>
                </div>
            </div>

            <!-- Embed Code -->
            <div class="p-4 rounded-lg  border-2 border-grey-300 bg-white shadow">
                <h3 class="text-lg font-semibold mb-4">{{ $t('label.embed_code') }}</h3>
                <StyledTextArea v-model="generatedCode" readonly style="width:100%;height:120px;" />
                <div class="flex justify-between mt-10">
                    <button class="btn text-white px-3 py-1 rounded" @click="generateWidgetCode">{{ $t('label.generate_code') }}</button>
                    <button class="border custom-tab-btn-outlined px-3 py-1 rounded" @click="copyCode">{{ $t('label.copy_code') }}</button>
                </div>
            </div>

            <!-- Quick Links -->
            <!-- <div class="p-4 rounded-lg border-2 border-grey-300 bg-white shadow">
                <h3 class="text-lg font-semibold mb-4">{{ $t('label.quick_links') }}</h3>
                <ul class="list-disc pl-5 space-y-1 text-blue-600">
                    <li><a href="#">{{ $t('label.account_overview') }}</a></li>
                    <li><a href="#">{{ $t('label.orders_management') }}</a></li>
                    <li><a href="#">{{ $t('label.widget_documentation') }}</a></li>
                </ul>
            </div> -->
        </div>
    </div>
</MainContentLayout>
</template>

<style scoped>
    .custom-border {
        border: 2px solid #ccc;
        border-radius: 10px;
    }

    .mask-circle {
        border-radius: 50%;
    }

    .mask-square {
        border-radius: 12px;
    }

    .mask-hexagon {
        clip-path: polygon(
            25% 5%, 75% 5%,
            100% 50%, 75% 95%,
            25% 95%, 0% 50%
        );
    }

    .mask-shield {
        clip-path: polygon(
            50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%
        );
    }

    .custom-tab-btn {
        background: #00594C !important;
    }
</style>
